import React, { useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Dialog, Slider } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import StopIcon from "@mui/icons-material/Stop";
import YouTubeIcon from "@mui/icons-material/YouTube";
import {
  fetchYoutubeInfo,
  formatClock,
  startYoutubeImport,
} from "./signageApi";
import { ErrorNote, ICON_BTN_SM, INPUT } from "./signageUi";

// "Add from YouTube": paste a link, preview it in YouTube's own player, drag
// the two slider handles to choose the part to keep (at most 3 minutes), and
// the server downloads just that section into the library as an MP4
// (server/routes/api/Signage/signageYoutube.js).

// YouTube's IFrame Player API, loaded once per page.
let apiPromise = null;
const loadYouTubeApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === "function") previous();
        resolve(window.YT);
      };
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.onerror = () => {
        apiPromise = null;
        reject(new Error("Couldn't load the YouTube player"));
      };
      document.head.appendChild(script);
    });
  }
  return apiPromise;
};

/**
 * Embedded preview. Exposes the YT.Player through `playerRef` so the slider
 * can seek it. Some videos forbid embedding — the preview then says so, but
 * the clip can still be picked and imported.
 */
const YoutubePreview = ({ videoId, playerRef }) => {
  const hostRef = useRef(null);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    let player = null;
    let cancelled = false;
    const host = hostRef.current;
    setFailure(null);
    loadYouTubeApi()
      .then((YT) => {
        if (cancelled || !host) return;
        const mount = document.createElement("div");
        host.appendChild(mount);
        player = new YT.Player(mount, {
          videoId,
          width: "100%",
          height: "100%",
          host: "https://www.youtube-nocookie.com",
          playerVars: { rel: 0, modestbranding: 1, playsinline: 1 },
          events: {
            onReady: () => {
              if (!cancelled) playerRef.current = player;
            },
            onError: (e) =>
              setFailure(
                [101, 150].includes(e.data)
                  ? "This video's owner doesn't allow previews outside YouTube — you can still pick the times and import it."
                  : "The preview couldn't play this video."
              ),
          },
        });
      })
      .catch((err) => setFailure(err.message));
    return () => {
      cancelled = true;
      playerRef.current = null;
      try {
        player?.destroy();
      } catch {
        /* already gone */
      }
      host?.replaceChildren();
    };
  }, [videoId, playerRef]);

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black">
      <div ref={hostRef} className="absolute inset-0 [&_iframe]:h-full [&_iframe]:w-full" />
      {failure && (
        <div className="absolute inset-x-0 bottom-0 bg-black/75 px-3 py-2 text-xs text-white">
          {failure}
        </div>
      )}
    </div>
  );
};

// "1:23", "01:02:03", or "83" → seconds; null when it isn't a time.
const parseClock = (text) => {
  const t = String(text || "").trim();
  if (!t) return null;
  if (/^\d+(\.\d+)?$/.test(t)) return Math.floor(Number(t));
  const parts = t.split(":");
  if (parts.length > 3 || parts.some((p) => !/^\d{1,2}$/.test(p))) return null;
  return parts.reduce((sum, p) => sum * 60 + Number(p), 0);
};

const looksLikeYoutube = (text) => /(youtube\.com|youtu\.be)\//i.test(String(text || ""));

// One end of the clip: a typeable time with ±1s nudges and "use playhead".
const TimeField = ({ label, value, onChange, onPlayhead, min, max }) => {
  const [draft, setDraft] = useState(null); // text while typing
  const commit = () => {
    if (draft == null) return;
    const v = parseClock(draft);
    if (v != null) onChange(Math.min(Math.max(v, min), max));
    setDraft(null);
  };
  return (
    <div className="app-card flex-1 rounded-xl border px-3 py-2.5 shadow-none">
      <p className="app-eyebrow">{label}</p>
      <div className="mt-1 flex items-center gap-1">
        <button
          type="button"
          className={ICON_BTN_SM}
          title="1 second earlier"
          aria-label={`${label}: 1 second earlier`}
          disabled={value <= min}
          onClick={() => onChange(value - 1)}
        >
          <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
        </button>
        <input
          className="app-input w-16 rounded-lg border px-1.5 py-1 text-center font-mono text-sm tabular-nums"
          value={draft ?? formatClock(value)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), commit())}
          aria-label={label}
        />
        <button
          type="button"
          className={ICON_BTN_SM}
          title="1 second later"
          aria-label={`${label}: 1 second later`}
          disabled={value >= max}
          onClick={() => onChange(value + 1)}
        >
          <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
        </button>
        <button
          type="button"
          className="app-accent ml-auto flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition hover:bg-sky-500/10"
          title="Set to where the video is now"
          onClick={onPlayhead}
        >
          <MyLocationIcon sx={{ fontSize: 14 }} /> Now
        </button>
      </div>
    </div>
  );
};

const YoutubeImportDialog = ({ scope, userId, onClose, onQueued }) => {
  const [url, setUrl] = useState("");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState([0, 0]);
  const [filename, setFilename] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [playhead, setPlayhead] = useState(null); // seconds, while the player is up
  const [previewing, setPreviewing] = useState(false);
  const playerRef = useRef(null);
  const stopTimer = useRef(null);

  const maxClip = info?.maxClipSeconds || 180;
  const duration = info?.duration || 0;
  const [start, end] = range;
  const length = end - start;

  useEffect(() => () => clearInterval(stopTimer.current), []);

  // Follow the player's position so the timeline can show a playhead.
  useEffect(() => {
    if (!info) return undefined;
    const timer = setInterval(() => {
      const p = playerRef.current;
      if (!p?.getCurrentTime) return;
      setPlayhead(p.getCurrentTime() || 0);
    }, 250);
    return () => clearInterval(timer);
  }, [info]);

  const load = async (link = url) => {
    if (!String(link).trim()) return;
    setLoading(true);
    setError(null);
    try {
      const next = await fetchYoutubeInfo(String(link).trim(), userId);
      setInfo(next);
      setRange([0, Math.min(next.duration, next.maxClipSeconds || 180)]);
      setFilename(next.title);
      setPlayhead(null);
    } catch (err) {
      setInfo(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const player = () => playerRef.current;

  const stopPreview = () => {
    clearInterval(stopTimer.current);
    setPreviewing(false);
  };

  const seek = (seconds, final) => {
    const p = player();
    if (!p?.seekTo) return;
    stopPreview();
    p.pauseVideo?.();
    p.seekTo(seconds, final);
  };

  // Keep the selection valid: at least 1s, at most the cap, inside the video.
  // `moved` says which end the user changed so the other one follows it.
  const setClip = (s, en, moved) => {
    s = Math.max(0, Math.min(s, duration - 1));
    en = Math.min(duration, Math.max(en, 1));
    if (en - s > maxClip) {
      if (moved === "start") en = s + maxClip;
      else s = en - maxClip;
    }
    if (en - s < 1) {
      if (moved === "start") en = Math.min(duration, s + 1);
      else s = Math.max(0, en - 1);
    }
    setRange([s, en]);
  };

  const onSlide = (_e, value, activeThumb) => {
    const moved = activeThumb === 0 ? "start" : "end";
    setClip(value[0], value[1], moved);
    seek(activeThumb === 0 ? value[0] : value[1], false);
  };

  const now = () => Math.floor(player()?.getCurrentTime?.() || 0);

  // Play just the selection, stopping at its end.
  const togglePreview = () => {
    const p = player();
    if (!p) return;
    if (previewing) {
      p.pauseVideo();
      stopPreview();
      return;
    }
    clearInterval(stopTimer.current);
    p.seekTo(start, true);
    p.playVideo();
    setPreviewing(true);
    stopTimer.current = setInterval(() => {
      if ((p.getCurrentTime?.() || 0) >= end) {
        p.pauseVideo();
        stopPreview();
      }
    }, 150);
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await startYoutubeImport(
        scope.id,
        { url: url.trim(), start, end, filename: filename.trim() },
        userId
      );
      onQueued();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const lengths = [15, 30, 60, maxClip].filter((n, i, all) => n <= duration && all.indexOf(n) === i);
  const libraryName = scope.company ? "the company library" : `${scope.name}'s library`;
  const pct = (sec) => (duration ? (sec / duration) * 100 : 0);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <div className="flex max-h-[92vh] flex-col">
        {/* Header */}
        <div className="app-divider flex items-center gap-3 border-b px-5 py-4">
          <span className="app-icon-tile h-10 w-10 rounded-xl bg-red-500/10">
            <YouTubeIcon sx={{ color: "#ff0000", fontSize: 26 }} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold tracking-tight">Add a clip from YouTube</h3>
            <p className="app-muted text-xs">
              Pick up to {formatClock(maxClip)} of any video — it's saved to{" "}
              {libraryName} as an MP4.
            </p>
          </div>
          <button
            type="button"
            className="app-icon-btn -mr-2"
            title="Close"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Link */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              load();
            }}
            className="flex gap-2"
          >
            <input
              className={INPUT}
              aria-label="YouTube link"
              placeholder="Paste a YouTube link — youtube.com/watch?v=…, youtu.be/…, or a Shorts link"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onPaste={(e) => {
                // Pasting a link loads it right away.
                const text = e.clipboardData.getData("text");
                if (looksLikeYoutube(text)) {
                  e.preventDefault();
                  setUrl(text.trim());
                  load(text);
                }
              }}
              autoFocus
            />
            <Button
              type="submit"
              variant={info ? "outlined" : "contained"}
              disabled={loading || !url.trim()}
              className="shrink-0"
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : info ? "Load another" : "Load video"}
            </Button>
          </form>
          <ErrorNote>{error}</ErrorNote>

          {!info ? (
            <div className="app-divider flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 text-center">
              {loading ? (
                <>
                  <CircularProgress size={28} />
                  <p className="app-muted text-sm">Looking up the video…</p>
                </>
              ) : (
                <>
                  <YouTubeIcon sx={{ fontSize: 44, color: "#ff0000", opacity: 0.8 }} />
                  <p className="font-medium">Paste a YouTube link to get started</p>
                  <p className="app-muted max-w-sm text-xs">
                    You'll see the video here and pick the part to keep. Only
                    import videos Big C owns or has permission to use.
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              <YoutubePreview videoId={info.videoId} playerRef={playerRef} />

              <div className="flex items-baseline justify-between gap-3">
                <p className="min-w-0 truncate font-semibold" title={info.title}>
                  {info.title}
                </p>
                <p className="app-muted shrink-0 text-xs tabular-nums">
                  {info.channel ? `${info.channel} · ` : ""}
                  {formatClock(duration)}
                </p>
              </div>

              {/* Timeline */}
              <div className="app-surface space-y-3 rounded-2xl border px-4 pb-4 pt-3">
                <div className="relative">
                  {playhead != null && (
                    <div
                      className="pointer-events-none absolute bottom-3 top-3 z-10 w-0.5 rounded bg-red-500"
                      style={{ left: `calc(${pct(Math.min(playhead, duration))}% )` }}
                      title="Playhead"
                    />
                  )}
                  <Slider
                    value={range}
                    min={0}
                    max={duration}
                    step={1}
                    onChange={onSlide}
                    onChangeCommitted={(_e, v) => seek(v[0], true)}
                    valueLabelDisplay="auto"
                    valueLabelFormat={formatClock}
                    disableSwap
                    getAriaLabel={(i) => (i === 0 ? "Clip start" : "Clip end")}
                    getAriaValueText={formatClock}
                  />
                  <div className="app-muted -mt-1 flex justify-between font-mono text-[10px]">
                    <span>0:00</span>
                    {playhead != null && <span className="text-red-500">▶ {formatClock(playhead)}</span>}
                    <span>{formatClock(duration)}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <TimeField
                    label="Start"
                    value={start}
                    min={0}
                    max={duration - 1}
                    onChange={(v) => {
                      setClip(v, end, "start");
                      seek(v, true);
                    }}
                    onPlayhead={() => setClip(now(), end, "start")}
                  />
                  <TimeField
                    label="End"
                    value={end}
                    min={1}
                    max={duration}
                    onChange={(v) => {
                      setClip(start, v, "end");
                      seek(v, true);
                    }}
                    onPlayhead={() => setClip(start, now(), "end")}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={previewing ? <StopIcon /> : <PlayArrowIcon />}
                    onClick={togglePreview}
                  >
                    {previewing ? "Stop" : "Preview clip"}
                  </Button>
                  <span className="app-muted text-xs">Length:</span>
                  {lengths.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setClip(start, start + n, "end")}
                      aria-pressed={length === n}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold tabular-nums transition ${
                        length === n
                          ? "border-sky-500/50 bg-sky-500/15 text-sky-700 dark:text-sky-300"
                          : "app-input hover:border-sky-400"
                      }`}
                    >
                      {n === maxClip ? `${formatClock(n)} max` : n < 60 ? `${n}s` : formatClock(n)}
                    </button>
                  ))}
                  <span
                    className={`ml-auto text-sm font-semibold tabular-nums ${
                      length >= maxClip ? "text-amber-600 dark:text-amber-400" : ""
                    }`}
                    title={`Clips can be up to ${formatClock(maxClip)}`}
                  >
                    {formatClock(length)} clip
                  </span>
                </div>
              </div>

              <label className="block">
                <span className="app-muted text-xs font-medium">Save as</span>
                <div className="mt-1 flex items-center gap-1.5">
                  <input
                    className={INPUT}
                    value={filename}
                    onChange={(e) => setFilename(e.target.value)}
                  />
                  <span className="app-muted text-sm">.mp4</span>
                </div>
              </label>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="app-divider flex flex-wrap items-center gap-3 border-t px-5 py-3">
          <p className="app-muted min-w-0 flex-1 text-xs">
            {info
              ? "Importing takes a minute or two — you can keep working, and it shows on the Media tab when it's done."
              : "Only import videos Big C owns or has permission to use."}
          </p>
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!info || saving || length < 1 || length > maxClip}
              onClick={submit}
            >
              {saving ? "Starting…" : info ? `Import ${formatClock(length)} clip` : "Import clip"}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default YoutubeImportDialog;
